import React, { useState, useEffect, useContext } from 'react';
import api from '../../api'; // Import the configured API instance with auth
import Swal from 'sweetalert2';
import ExcelJS from 'exceljs';
import Select from 'react-select';
import { AuthContext } from '../../components/AuthContext';
import { getCurrentMonthDates } from '../../utils/dateUtils';
import EChart from '../../components/charts/EChart';
import LazyMount from '../../components/charts/LazyMount';
import { pieOption, barOption } from '../../components/charts/chartOptions';
import { ChartCard, EmptyState, TrendCard, SearchInput } from '../../components/dashboard/DashboardPrimitives';

const RemindersManagement = () => {
  const [reminders, setReminders] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [totalReminders, setTotalReminders] = useState(0);
  
  const monthDates = getCurrentMonthDates();
  const [startDate, setStartDate] = useState(monthDates.startDate);
  const [endDate, setEndDate] = useState(monthDates.endDate);
  const [status, setStatus] = useState('');
  const [category, setCategory] = useState('');
  const [datatype, setDatatype] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    cancelled: 0,
    followUp: 0,
    partialComplete: 0,
    partiallyClosed: 0
  });
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [selectedItemsReminder, setSelectedItemsReminder] = useState(null);
  const [showQuotationModal, setShowQuotationModal] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [quotationType, setQuotationType] = useState('');
  const [loadingReminders, setLoadingReminders] = useState({}); // Object to track loading state per reminder
  const [selectedReminders, setSelectedReminders] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedReminderHistory, setSelectedReminderHistory] = useState(null);
  const [showPartialItemsModal, setShowPartialItemsModal] = useState(false);
  const [partialItemsData, setPartialItemsData] = useState(null);
  const [selectedPartialItems, setSelectedPartialItems] = useState([]);
  const [partialReminderData, setPartialReminderData] = useState(null);
  const [selectedSalesmanFilter, setSelectedSalesmanFilter] = useState([]);
  const [availableSalesmenFilter, setAvailableSalesmenFilter] = useState([]);
  const { user } = useContext(AuthContext);

  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  const fetchReminders = async () => {
    setLoading(true);
    try {
      // If we have a search query, we need to fetch all results and handle pagination client-side
      const params = { 
        page: searchQuery ? 0 : page, 
        limit: searchQuery ? 1000 : limit, // Fetch more results when searching
        startDate, 
        endDate, 
        status, 
        category,
        datatype,
        sortBy,
        sortOrder,
        salesmanIds: selectedSalesmanFilter.length > 0 ? selectedSalesmanFilter.join(',') : undefined // Add salesman filter as comma-separated string
      };
      
      // Filter out empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/reminders/${user.user._id}`, {
        params
      });
      
      let filteredReminders = response.data.reminders;
      let totalFilteredReminders = response.data.totalReminders;
      
      // Apply client-side filters
      if (searchQuery) {
        filteredReminders = filteredReminders.filter(reminder =>
          reminder.message?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          reminder.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          reminder.datatype?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          getRecipientName(reminder)?.toLowerCase().includes(searchQuery.toLowerCase())
        );
        totalFilteredReminders = filteredReminders.length;
        
        // Apply client-side pagination for search results
        const startIndex = page * limit;
        const endIndex = startIndex + limit;
        filteredReminders = filteredReminders.slice(startIndex, endIndex);
      }
      
      setReminders(filteredReminders);
      setTotalReminders(totalFilteredReminders);
    } catch (error) {
      Swal.fire('Error', 'Failed to fetch reminders', 'error');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setStatus('');
    setCategory('');
    setDatatype('');
    setSortBy('date');
    setSortOrder('desc');
    setSearchQuery('');
    setSelectedSalesmanFilter([]);
    setPage(0);
  };

  const fetchStats = async () => {
    try {
      const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/reminders/stats/${user.user._id}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchSalesmenForFilter = async () => {
    try {
      // Only fetch if datatype is QuotationNew or TallyQuotation
      if (datatype === 'QuotationNew' || datatype === 'TallyQuotation') {
        const userCompanyId = user?.user?.company._id || user?.user?.company;
        const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/employees/company2/${userCompanyId}`);
        setAvailableSalesmenFilter(response.data.employees || []);
      } else {
        setAvailableSalesmenFilter([]);
        setSelectedSalesmanFilter([]);
      }
    } catch (error) {
      console.error('Failed to fetch salesmen for filter:', error);
      setAvailableSalesmenFilter([]);
    }
  };

  const fetchQuotationDetails = async (quotationId, datatype) => {
    try {
      let endpoint = '';
      switch (datatype) {
        case 'QuotationNew':
          endpoint = `/quotation-new/data/${quotationId}`;
          break;
        case 'TallyQuotation':
          endpoint = `/tally-quotation/${quotationId}`;
          break;
        default:
          throw new Error('Invalid quotation type');
      }
      
      // Use authenticated API instance
      const response = await api.get(endpoint);
      return response.data;
    } catch (error) {
      console.error('Error fetching quotation details:', error);
      Swal.fire('Error', 'Failed to fetch quotation details', 'error');
      return null;
    }
  };

  // Helper function to create TallyQuotation Excel export
  const createTallyQuotationExcel = async (workbook, quotationData) => {
    const worksheet = workbook.addWorksheet('TallyQuotation Details');
    
    // Set column widths
    worksheet.columns = [
      { header: 'Field', key: 'field', width: 25 },
      { header: 'Value', key: 'value', width: 50 }
    ];
    
    // Add header styling
    worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
    worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '366092' } };
    
    // Add TallyQuotation basic information (different fields than QuotationNew)
    worksheet.addRow({ field: 'Quotation ID', value: quotationData.id || 'N/A' });
    worksheet.addRow({ field: 'Status', value: quotationData.status || 'N/A' });
    worksheet.addRow({ field: 'Created Date', value: quotationData.createdAt ? new Date(quotationData.createdAt).toLocaleDateString() : 'N/A' });
    worksheet.addRow({ field: 'Site Address', value: quotationData.siteAddress || 'N/A' });
    worksheet.addRow({ field: 'Sub Total', value: quotationData.subTotal || 0 });
    worksheet.addRow({ field: 'Total GST', value: quotationData.totalGST || 0 });
    worksheet.addRow({ field: 'Total Amount', value: quotationData.totalAmount || 0 });
    worksheet.addRow({ field: 'Offer Price Grand Total', value: quotationData.offerPriceGrandTotal || 0 });
    worksheet.addRow({ field: 'Listed Price Grand Total', value: quotationData.listedPriceGrandTotal || 0 });
    worksheet.addRow({ field: 'Cartage', value: quotationData.cartage || 0 });
    worksheet.addRow({ field: 'Packing & Forwarding', value: quotationData.packingAndForwarding || 0 });
    worksheet.addRow({ field: 'Labour', value: quotationData.labour || 0 });
    worksheet.addRow({ field: 'Round Off', value: quotationData.roundOff || 0 });
    worksheet.addRow({ field: 'Remarks', value: quotationData.remarks || 'N/A' });
    
    // Add recipient information
    if (quotationData.recipient) {
      worksheet.addRow({ field: '', value: '' }); // Empty row
      worksheet.addRow({ field: 'RECIPIENT DETAILS', value: '' });
      worksheet.getRow(worksheet.rowCount).font = { bold: true };
      
      worksheet.addRow({ field: 'Recipient Name', value: quotationData.recipient.name || quotationData.recipient.firmName || 'N/A' });
      worksheet.addRow({ field: 'Email', value: quotationData.recipient.email || 'N/A' });
      worksheet.addRow({ field: 'Phone', value: quotationData.recipient.phone || 'N/A' });
      worksheet.addRow({ field: 'Address', value: quotationData.recipient.address || 'N/A' });
    }
    
    // Add salesmen information
    if (quotationData.salesmen && quotationData.salesmen.length > 0) {
      worksheet.addRow({ field: '', value: '' }); // Empty row
      worksheet.addRow({ field: 'SALESMEN', value: '' });
      worksheet.getRow(worksheet.rowCount).font = { bold: true };
      
      quotationData.salesmen.forEach((salesman, index) => {
        worksheet.addRow({ field: `Salesperson ${index + 1}`, value: salesman.name || 'N/A' });
      });
    }
    
    // Add TallyItems details in a separate worksheet
    if (quotationData.sections && quotationData.sections.length > 0) {
      const itemsWorksheet = workbook.addWorksheet('TallyItems Details');
      
      itemsWorksheet.columns = [
        { header: 'Section', key: 'section', width: 15 },
        { header: 'Item Name', key: 'itemName', width: 30 },
        { header: 'Custom Name', key: 'customName', width: 25 },
        { header: 'Application Area', key: 'applicationArea', width: 20 },
        { header: 'Boxes', key: 'boxes', width: 10 },
        { header: 'Pieces', key: 'pieces', width: 10 },
        { header: 'Quantity', key: 'quantity', width: 10 },
        { header: 'Unit', key: 'unit', width: 10 },
        { header: 'Listed Price', key: 'listedPrice', width: 12 },
        { header: 'Discount %', key: 'discount', width: 12 },
        { header: 'Offer Price', key: 'offerPrice', width: 12 },
        { header: 'Amount', key: 'amount', width: 12 },
        { header: 'Offer Price Total', key: 'offerPriceTotal', width: 15 },
        { header: 'Coverage Area', key: 'coverageArea', width: 12 },
        { header: 'Coverage Unit', key: 'coverageAreaUnit', width: 12 }
      ];
      
      // Style items header
      itemsWorksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      itemsWorksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '366092' } };
      
      quotationData.sections.forEach((section, sectionIndex) => {
        section.items.forEach((item) => {
          itemsWorksheet.addRow({
            section: section.name || `Section ${sectionIndex + 1}`,
            itemName: item.itemName || item.item?.Name || 'N/A',
            customName: item.customName || 'N/A',
            applicationArea: item.applicationArea || 'N/A',
            boxes: item.boxes || 0,
            pieces: item.pieces || 0,
            quantity: item.quantity || 0,
            unit: item.unit || 'N/A',
            listedPrice: item.listedPrice || 0,
            discount: item.discount || 0,
            offerPrice: item.offerPrice || 0,
            amount: item.amount || 0,
            offerPriceTotal: item.offerPriceTotal || 0,
            coverageArea: item.coverageArea || 0,
            coverageAreaUnit: item.coverageAreaUnit || 'N/A'
          });
        });
      });
    }
  };

  // Helper function to create QuotationNew Excel export
  const createQuotationNewExcel = async (workbook, quotationData) => {
    const worksheet = workbook.addWorksheet('QuotationNew Details');
    
    // Set column widths
    worksheet.columns = [
      { header: 'Field', key: 'field', width: 25 },
      { header: 'Value', key: 'value', width: 50 }
    ];
    
    // Add header styling
    worksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
    worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '366092' } };
    
    // Add QuotationNew basic information
    worksheet.addRow({ field: 'Quotation ID', value: quotationData.id || 'N/A' });
    worksheet.addRow({ field: 'Status', value: quotationData.status || 'N/A' });
    worksheet.addRow({ field: 'Created Date', value: quotationData.createdAt ? new Date(quotationData.createdAt).toLocaleDateString() : 'N/A' });
    worksheet.addRow({ field: 'Valid Till', value: quotationData.validTill ? new Date(quotationData.validTill).toLocaleDateString() : 'N/A' });
    worksheet.addRow({ field: 'Total Amount', value: quotationData.totalAmount || 0 });
    worksheet.addRow({ field: 'Sub Total', value: quotationData.subTotal || 0 });
    worksheet.addRow({ field: 'Special Discount', value: quotationData.specialDiscount || 0 });
    worksheet.addRow({ field: 'Cartage', value: quotationData.cartage || 0 });
    worksheet.addRow({ field: 'Labour', value: quotationData.labour || 0 });
    worksheet.addRow({ field: 'Packing & Forwarding', value: quotationData.packingAndForwarding || 0 });
    worksheet.addRow({ field: 'Round Off', value: quotationData.roundOff || 0 });
    worksheet.addRow({ field: 'Remarks', value: quotationData.remarks || 'N/A' });
    worksheet.addRow({ field: 'Site Address', value: quotationData.siteAddress || 'N/A' });
    worksheet.addRow({ field: 'Pricing Type', value: quotationData.pricingType || 'N/A' });
    worksheet.addRow({ field: 'Print Type', value: quotationData.printType || 'N/A' });
    
    // Add recipient information
    if (quotationData.recipient) {
      worksheet.addRow({ field: '', value: '' }); // Empty row
      worksheet.addRow({ field: 'RECIPIENT DETAILS', value: '' });
      worksheet.getRow(worksheet.rowCount).font = { bold: true };
      
      worksheet.addRow({ field: 'Recipient Name', value: quotationData.recipient.name || quotationData.recipient.firmName || 'N/A' });
      worksheet.addRow({ field: 'Email', value: quotationData.recipient.email || 'N/A' });
      worksheet.addRow({ field: 'Phone', value: quotationData.recipient.phone || 'N/A' });
      worksheet.addRow({ field: 'Address', value: quotationData.recipient.address || 'N/A' });
    }
    
    // Add salesmen information
    if (quotationData.salesmen && quotationData.salesmen.length > 0) {
      worksheet.addRow({ field: '', value: '' }); // Empty row
      worksheet.addRow({ field: 'SALESMEN', value: '' });
      worksheet.getRow(worksheet.rowCount).font = { bold: true };
      
      quotationData.salesmen.forEach((salesman, index) => {
        worksheet.addRow({ field: `Salesperson ${index + 1}`, value: salesman.name || 'N/A' });
      });
    }
    
    // Add Items details in a separate worksheet
    if (quotationData.sections && quotationData.sections.length > 0) {
      const itemsWorksheet = workbook.addWorksheet('Items Details');
      
      itemsWorksheet.columns = [
        { header: 'Section', key: 'section', width: 15 },
        { header: 'Item Name', key: 'itemName', width: 30 },
        { header: 'Custom Name', key: 'customName', width: 25 },
        { header: 'SKU Code', key: 'skuCode', width: 20 },
        { header: 'Brand', key: 'brand', width: 15 },
        { header: 'Application Area', key: 'applicationArea', width: 20 },
        { header: 'Coverage Area', key: 'coverageArea', width: 12 },
        { header: 'Coverage Unit', key: 'coverageAreaUnit', width: 12 },
        { header: 'Boxes', key: 'boxes', width: 10 },
        { header: 'Pieces', key: 'pieces', width: 10 },
        { header: 'Quantity', key: 'quantity', width: 10 },
        { header: 'Unit', key: 'unit', width: 10 },
        { header: 'Offer Type', key: 'offerType', width: 12 },
        { header: 'Listed Price', key: 'listedPrice', width: 12 },
        { header: 'Discount %', key: 'discount', width: 12 },
        { header: 'Offer Price', key: 'offerPrice', width: 12 },
        { header: 'Offer Price Total', key: 'offerPriceTotal', width: 15 },
        { header: 'Narration', key: 'narration', width: 20 }
      ];
      
      // Style items header
      itemsWorksheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      itemsWorksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '366092' } };
      
      quotationData.sections.forEach((section, sectionIndex) => {
        section.items.forEach((item) => {
          itemsWorksheet.addRow({
            section: section.name || `Section ${sectionIndex + 1}`,
            itemName: item.item?.skuDescription || item.itemName || 'N/A',
            customName: item.customName || 'N/A',
            skuCode: item.item?.skuCode || 'N/A',
            brand: item.item?.brand?.name || 'N/A',
            applicationArea: item.applicationArea || 'N/A',
            coverageArea: item.coverageArea || 0,
            coverageAreaUnit: item.coverageAreaUnit || 'N/A',
            boxes: item.boxes || 0,
            pieces: item.pieces || 0,
            quantity: item.quantity || 0,
            unit: item.unit || 'N/A',
            offerType: item.offerType || 'N/A',
            listedPrice: item.listedPrice || 0,
            discount: item.discount || 0,
            offerPrice: item.offerPrice || 0,
            offerPriceTotal: item.offerPriceTotal || 0,
            narration: item.narration || 'N/A'
          });
        });
      });
    }
  };

  const handleExportQuotation = async (reminder) => {
    if (!['QuotationNew', 'TallyQuotation'].includes(reminder.datatype)) {
      Swal.fire('Error', 'Export is only available for quotations', 'error');
      return;
    }

    const reminderId = reminder._id || reminder.dataid._id;
    const quotationId = reminder.dataid._id;
    let endpoint = '';
    
    try {
      setLoadingReminders(prev => ({ ...prev, [reminderId]: 'export' }));
      
      console.log('🔧 Export Debug Info:', {
        reminderType: reminder.datatype,
        quotationId: quotationId,
        reminderId: reminderId
      });
      
      switch (reminder.datatype) {
        case 'QuotationNew':
          endpoint = `${process.env.REACT_APP_API_BASE_URL}/quotation-new/data/${quotationId}`;
          break;
        case 'TallyQuotation':
          // Fix: Use the correct endpoint for TallyQuotation (/:id instead of /data/:id)
          endpoint = `${process.env.REACT_APP_API_BASE_URL}/tally-quotation/${quotationId}`;
          break;
        default:
          throw new Error('Invalid quotation type for export');
      }
      
      console.log('📡 Making API request to:', endpoint);
      
      // Use the authenticated API instance instead of plain axios
      const response = await api.get(endpoint.replace(process.env.REACT_APP_API_BASE_URL, ''));
      
      console.log('✅ API Response received:', {
        status: response.status,
        hasData: !!response.data,
        dataKeys: Object.keys(response.data || {})
      });
      
      const quotationData = response.data;
      
      // Create Excel workbook with separate handlers for different quotation types
      const workbook = new ExcelJS.Workbook();
      
      if (reminder.datatype === 'TallyQuotation') {
        await createTallyQuotationExcel(workbook, quotationData);
      } else if (reminder.datatype === 'QuotationNew') {
        await createQuotationNewExcel(workbook, quotationData);
      }
      
      // Generate and download the Excel file
      const buffer = await workbook.xlsx.writeBuffer();
      const filename = `${reminder.datatype}_${quotationId}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      Swal.fire('Success', `${reminder.datatype} exported to Excel successfully`, 'success');
    } catch (error) {
      console.error('❌ Export Error Details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        endpoint: endpoint,
        quotationType: reminder.datatype,
        quotationId: quotationId
      });
      
      // Create detailed error message
      let errorMessage = 'Failed to export quotation';
      let errorDetails = '';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const serverMessage = error.response.data?.message || error.response.data?.error || error.response.statusText;
        
        switch (status) {
          case 401:
            errorMessage = 'Authentication failed';
            errorDetails = 'Please log in again and try the export';
            break;
          case 403:
            errorMessage = 'Access denied';
            errorDetails = 'You do not have permission to export this quotation';
            break;
          case 404:
            errorMessage = 'Quotation not found';
            errorDetails = `Quotation ${quotationId} could not be found in the system`;
            break;
          case 500:
            errorMessage = 'Server error during export';
            errorDetails = serverMessage || 'Internal server error occurred';
            break;
          default:
            errorMessage = `Export failed (${status})`;
            errorDetails = serverMessage || `HTTP ${status} error`;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network connection failed';
        errorDetails = 'Please check your internet connection and try again';
      } else {
        // Other error
        errorMessage = 'Export processing failed';
        errorDetails = error.message || 'Unknown error occurred';
      }
      
      Swal.fire({
        title: 'Export Failed',
        html: `
          <div style="text-align: left;">
            <p><strong>Error:</strong> ${errorMessage}</p>
            <p><strong>Details:</strong> ${errorDetails}</p>
            <hr>
            <p><strong>Debug Info:</strong></p>
            <p>• Quotation Type: ${reminder.datatype}</p>
            <p>• Quotation ID: ${quotationId}</p>
            <p>• Endpoint: ${endpoint}</p>
            ${error.response?.status ? `<p>• HTTP Status: ${error.response.status}</p>` : ''}
          </div>
        `,
        icon: 'error',
        confirmButtonText: 'Close',
        width: '500px'
      });
    } finally {
      setLoadingReminders(prev => {
        const newState = { ...prev };
        delete newState[reminderId];
        return newState;
      });
    }
  };

  const handleViewQuotation = async (reminder) => {
    if (!['QuotationNew', 'TallyQuotation'].includes(reminder.datatype)) {
      Swal.fire('Error', 'This reminder is not for a quotation', 'error');
      return;
    }

    const reminderId = reminder._id || reminder.dataid._id;
    setLoadingReminders(prev => ({ ...prev, [reminderId]: 'view' }));
    
    const quotationData = await fetchQuotationDetails(reminder.dataid._id, reminder.datatype);
    
    setLoadingReminders(prev => {
      const newState = { ...prev };
      delete newState[reminderId];
      return newState;
    });
    
    if (quotationData) {
      setSelectedQuotation(quotationData);
      setQuotationType(reminder.datatype);
      setShowQuotationModal(true);
    }
  };

  useEffect(() => {
    fetchReminders();
    fetchStats();
  }, [page, limit, startDate, endDate, status, category, datatype, sortBy, sortOrder, searchQuery, selectedSalesmanFilter]);

  // Reset page when search query changes
  useEffect(() => {
    if (searchQuery !== '') {
      setPage(0);
    }
  }, [searchQuery]);

  // Fetch salesmen when datatype changes to QuotationNew or TallyQuotation
  useEffect(() => {
    fetchSalesmenForFilter();
  }, [datatype]);

  // Sync dark mode with localStorage
  useEffect(() => {
    const interval = setInterval(() => {
      const savedMode = localStorage.getItem('darkMode');
      setIsDarkMode(savedMode === 'true');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Dark mode: CSS injection for comprehensive styling
  useEffect(() => {
    const styleId = 'reminders-dark-mode-css';
    let el = document.getElementById(styleId);
    if (isDarkMode) {
      if (!el) {
        el = document.createElement('style');
        el.id = styleId;
        document.head.appendChild(el);
      }
      el.textContent = `
        /* === Base background/text/border overrides === */
        .rem-dark-page .bg-white { background-color: #1f2937 !important; }
        .rem-dark-page .bg-gray-50 { background-color: #374151 !important; }
        .rem-dark-page .bg-gray-100 { background-color: #374151 !important; }
        .rem-dark-page .text-gray-900 { color: #f3f4f6 !important; }
        .rem-dark-page .text-gray-800 { color: #e5e7eb !important; }
        .rem-dark-page .text-gray-700 { color: #d1d5db !important; }
        .rem-dark-page .text-gray-600 { color: #9ca3af !important; }
        .rem-dark-page .text-gray-500 { color: #6b7280 !important; }
        .rem-dark-page .text-gray-400 { color: #6b7280 !important; }
        .rem-dark-page .border-gray-200 { border-color: #374151 !important; }
        .rem-dark-page .border-gray-300 { border-color: #4b5563 !important; }
        .rem-dark-page .border-gray-100 { border-color: #374151 !important; }
        .rem-dark-page .border { border-color: #374151 !important; }
        .rem-dark-page .divide-gray-200 > * + * { border-color: #374151 !important; }
        .rem-dark-page .hover\\:bg-gray-50:hover { background-color: #4b5563 !important; }
        .rem-dark-page .hover\\:bg-gray-200:hover { background-color: #4b5563 !important; }
        .rem-dark-page .hover\\:border-gray-300:hover { border-color: #6b7280 !important; }

        /* === Form elements === */
        .rem-dark-page input:not([type=checkbox]):not([type=radio]) {
          background-color: #374151 !important; border-color: #4b5563 !important; color: #f3f4f6 !important;
        }
        .rem-dark-page input:not([type=checkbox]):not([type=radio])::placeholder { color: #9ca3af !important; }
        .rem-dark-page input:not([type=checkbox]):not([type=radio]):focus {
          border-color: #60a5fa !important; box-shadow: 0 0 0 2px rgba(96,165,250,0.2) !important;
        }
        .rem-dark-page select {
          background-color: #374151 !important; border-color: #4b5563 !important; color: #f3f4f6 !important;
        }
        .rem-dark-page select:focus {
          border-color: #60a5fa !important; box-shadow: 0 0 0 2px rgba(96,165,250,0.2) !important;
        }
        .rem-dark-page textarea {
          background-color: #374151 !important; border-color: #4b5563 !important; color: #f3f4f6 !important;
        }
        .rem-dark-page textarea::placeholder { color: #9ca3af !important; }
        .rem-dark-page label { color: #d1d5db !important; }
        .rem-dark-page input[type=checkbox] {
          background-color: #374151 !important; border-color: #4b5563 !important;
        }
        .rem-dark-page input[type=checkbox]:checked {
          background-color: #3b82f6 !important; border-color: #3b82f6 !important;
        }

        /* === Color-100 backgrounds === */
        .rem-dark-page .bg-blue-100 { background-color: rgba(30, 58, 138, 0.25) !important; }
        .rem-dark-page .bg-green-100 { background-color: rgba(6, 78, 59, 0.25) !important; }
        .rem-dark-page .bg-purple-100 { background-color: rgba(88, 28, 135, 0.25) !important; }
        .rem-dark-page .bg-indigo-100 { background-color: rgba(49, 46, 129, 0.25) !important; }
        .rem-dark-page .bg-orange-100 { background-color: rgba(124, 45, 18, 0.25) !important; }
        .rem-dark-page .bg-yellow-100 { background-color: rgba(113, 63, 18, 0.25) !important; }
        .rem-dark-page .bg-red-100 { background-color: rgba(127, 29, 29, 0.25) !important; }

        /* === Color-50 backgrounds === */
        .rem-dark-page .bg-blue-50 { background-color: rgba(30, 58, 138, 0.15) !important; }
        .rem-dark-page .bg-green-50 { background-color: rgba(6, 78, 59, 0.15) !important; }
        .rem-dark-page .bg-purple-50 { background-color: rgba(88, 28, 135, 0.15) !important; }
        .rem-dark-page .bg-orange-50 { background-color: rgba(124, 45, 18, 0.15) !important; }
        .rem-dark-page .bg-yellow-50 { background-color: rgba(113, 63, 18, 0.15) !important; }
        .rem-dark-page .bg-red-50 { background-color: rgba(127, 29, 29, 0.15) !important; }
        .rem-dark-page .bg-amber-50, .rem-dark-page .bg-amber-100 { background-color: rgba(120, 53, 15, 0.2) !important; }

        /* === Colored borders === */
        .rem-dark-page .border-blue-200 { border-color: #1e3a8a !important; }
        .rem-dark-page .border-green-200 { border-color: #064e3b !important; }
        .rem-dark-page .border-green-300 { border-color: #059669 !important; }
        .rem-dark-page .border-purple-200 { border-color: #581c87 !important; }
        .rem-dark-page .border-amber-200, .rem-dark-page .border-amber-300 { border-color: #92400e !important; }
        .rem-dark-page .border-amber-400 { border-color: #b45309 !important; }
        .rem-dark-page .border-orange-200, .rem-dark-page .border-orange-300 { border-color: #c2410c !important; }
        .rem-dark-page .border-yellow-400 { border-color: #ca8a04 !important; }
        .rem-dark-page .border-red-400 { border-color: #dc2626 !important; }

        /* === Colored text === */
        .rem-dark-page .text-blue-900 { color: #93c5fd !important; }
        .rem-dark-page .text-blue-700 { color: #93c5fd !important; }
        .rem-dark-page .text-blue-600 { color: #60a5fa !important; }
        .rem-dark-page .text-green-900 { color: #86efac !important; }
        .rem-dark-page .text-green-800 { color: #86efac !important; }
        .rem-dark-page .text-green-700 { color: #86efac !important; }
        .rem-dark-page .text-green-600 { color: #4ade80 !important; }
        .rem-dark-page .text-purple-900 { color: #d8b4fe !important; }
        .rem-dark-page .text-purple-800 { color: #d8b4fe !important; }
        .rem-dark-page .text-purple-600 { color: #c084fc !important; }
        .rem-dark-page .text-amber-800 { color: #fbbf24 !important; }
        .rem-dark-page .text-amber-600 { color: #fbbf24 !important; }
        .rem-dark-page .text-orange-800 { color: #fdba74 !important; }
        .rem-dark-page .text-orange-900 { color: #fdba74 !important; }
        .rem-dark-page .text-yellow-600 { color: #fbbf24 !important; }
        .rem-dark-page .text-yellow-800 { color: #fbbf24 !important; }
        .rem-dark-page .text-orange-600 { color: #fb923c !important; }
        .rem-dark-page .text-red-800 { color: #fca5a5 !important; }
        .rem-dark-page .text-red-600 { color: #f87171 !important; }
        .rem-dark-page .text-red-500 { color: #f87171 !important; }
        .rem-dark-page .text-indigo-800 { color: #a5b4fc !important; }
        .rem-dark-page .hover\\:bg-amber-200:hover { background-color: rgba(120, 53, 18, 0.35) !important; }

        /* === Gradient overrides === */
        .rem-dark-page .from-blue-50 { --tw-gradient-from: rgba(30, 58, 138, 0.15) !important; }
        .rem-dark-page .to-indigo-50 { --tw-gradient-to: rgba(49, 46, 129, 0.15) !important; }
        .rem-dark-page .from-green-50 { --tw-gradient-from: rgba(6, 78, 59, 0.15) !important; }
        .rem-dark-page .to-emerald-50 { --tw-gradient-to: rgba(6, 78, 59, 0.15) !important; }
        .rem-dark-page .from-purple-50 { --tw-gradient-from: rgba(88, 28, 135, 0.15) !important; }
        .rem-dark-page .to-pink-50 { --tw-gradient-to: rgba(112, 26, 117, 0.15) !important; }
        .rem-dark-page .from-gray-100 { --tw-gradient-from: #374151 !important; }
        .rem-dark-page .to-gray-50 { --tw-gradient-to: #374151 !important; }
        .rem-dark-page .from-gray-50 { --tw-gradient-from: #374151 !important; }
        .rem-dark-page .to-slate-50 { --tw-gradient-to: #1e293b !important; }

        /* === React Select === */
        .rem-dark-page [class*="-control"] { background-color: #374151 !important; border-color: #4b5563 !important; }
        .rem-dark-page [class*="-singleValue"] { color: #f3f4f6 !important; }
        .rem-dark-page [class*="-placeholder"] { color: #9ca3af !important; }
        .rem-dark-page [class*="-menu"] { background-color: #1f2937 !important; border: 1px solid #4b5563 !important; }
        .rem-dark-page [class*="-option"]:hover { background-color: #374151 !important; }
        .rem-dark-page [class*="-multiValue"] { background-color: #4b5563 !important; }
        .rem-dark-page [class*="-MultiValueGeneric"] { color: #f3f4f6 !important; }
        .rem-dark-page [class*="-MultiValueRemove"]:hover { background-color: #ef4444 !important; color: white !important; }
        .rem-dark-page [class*="-indicatorSeparator"] { background-color: #4b5563 !important; }

        /* === SweetAlert2 (renders outside page scope, use html.dark) === */
        html.dark .swal2-popup { background-color: #1f2937 !important; color: #f3f4f6 !important; }
        html.dark .swal2-title { color: #f3f4f6 !important; }
        html.dark .swal2-html-container { color: #d1d5db !important; }
        html.dark .swal2-html-container label { color: #d1d5db !important; }
        html.dark .swal2-html-container input,
        html.dark .swal2-html-container textarea {
          background-color: #374151 !important; border-color: #4b5563 !important; color: #f3f4f6 !important;
        }
        html.dark .swal2-input, html.dark .swal2-textarea {
          background-color: #374151 !important; border-color: #4b5563 !important; color: #f3f4f6 !important;
        }
        html.dark .swal2-validation-message {
          background-color: #374151 !important; color: #fbbf24 !important;
        }
      `;
    } else {
      if (el) el.remove();
    }
    return () => { const existing = document.getElementById(styleId); if (existing) existing.remove(); };
  }, [isDarkMode]);

  const handleStatusUpdate = async (id, newStatus, details) => {
    try {
      await api.put(`${process.env.REACT_APP_API_BASE_URL}/reminders/status/${id}`, {
        status: newStatus,
        details,
      });
      Swal.fire('Success', 'Reminder status updated', 'success');
      fetchReminders();
    } catch (error) {
      Swal.fire('Error', 'Failed to update reminder status', 'error');
    }
  };

  const submitPartialComplete = async (reminder, details) => {
    try {
      await api.put(
        `${process.env.REACT_APP_API_BASE_URL}/reminders/partial-complete/${reminder._id}`,
        {
          partialItems: details.partialItems || [],
          items: details.allItems || [],
          selectedKeys: details.selectedKeys || [],
          partialDate: details.partialDate,
          partialRemarks: details.partialRemarks || '',
          nextFollowUpDate: details.nextFollowUpDate || null,
          nextFollowUpRemarks: details.nextFollowUpRemarks || '',
          completedBy: user?.user?._id,
        },
      );
      Swal.fire('Success', 'Partial completion saved', 'success');
      fetchReminders();
      fetchStats();
    } catch (error) {
      console.error('Partial complete error:', error);
      Swal.fire('Error', 'Failed to save partial completion', 'error');
    }
  };

  const handlePartialComplete = async (reminder) => {
    // Check if this is a quotation reminder
    if (!['QuotationNew', 'TallyQuotation', 'Quotation'].includes(reminder.datatype)) {
      // For non-quotation reminders, show partial dialog with items when available
      const items = reminder.dataid?.itemsOfInterest || [];
      const itemsHtml = items.length
        ? `<div class="mb-4">
             <label class="block text-sm font-medium text-gray-700 mb-2">Items Completed:</label>
             <div class="max-h-40 overflow-y-auto bg-gray-50 p-2 rounded border">
               ${items
                 .map(
                   item =>
                     `<label class="flex items-center gap-2 py-1 text-sm"><input type="checkbox" class="partial-item" value="${item}" /> ${item}</label>`,
                 )
                 .join('')}
             </div>
           </div>`
        : '';

      const result = await Swal.fire({
        title: 'Mark as Partial Complete',
        html: `
          <div class="text-left">
            ${itemsHtml}
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Partial Completion Date:</label>
              <input type="date" id="partialDate" class="w-full p-2 border border-gray-300 rounded-md" required />
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Partial Remarks:</label>
              <textarea id="partialRemarks" class="w-full p-2 border border-gray-300 rounded-md h-24" placeholder="Enter partial remarks"></textarea>
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Next Follow-up Date (optional):</label>
              <input type="date" id="nextFollowUpDate" class="w-full p-2 border border-gray-300 rounded-md" />
            </div>
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Next Follow-up Remarks:</label>
              <textarea id="nextFollowUpRemarks" class="w-full p-2 border border-gray-300 rounded-md h-20" placeholder="Notes for the next follow-up"></textarea>
            </div>
          </div>`,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Mark Partial',
        cancelButtonText: 'Cancel',
        confirmButtonColor: '#f59e0b',
        preConfirm: () => {
          const partialDate = document.getElementById('partialDate').value;
          const partialRemarks = document.getElementById('partialRemarks').value;
          if (!partialDate) {
            Swal.showValidationMessage('Please enter partial completion date');
            return false;
          }
          if (!partialRemarks || !partialRemarks.trim()) {
            Swal.showValidationMessage('Please enter partial remarks');
            return false;
          }
          const selected = Array.from(
            document.querySelectorAll('.partial-item:checked'),
          ).map(el => el.value);
          return {
            partialItems: selected,
            partialDate,
            partialRemarks,
            nextFollowUpDate: document.getElementById('nextFollowUpDate').value || null,
            nextFollowUpRemarks: document.getElementById('nextFollowUpRemarks').value,
          };
        },
      });

      if (result.isConfirmed && result.value) {
        submitPartialComplete(reminder, result.value);
      }
      return;
    }

    // For quotation reminders, fetch quotation data and show item selection
    try {
      const quotationData = await fetchQuotationDetails(reminder.dataid._id, reminder.datatype);
      if (quotationData) {
        setPartialItemsData(quotationData);
        setPartialReminderData(reminder);
        setSelectedPartialItems([]);
        setShowPartialItemsModal(true);
      }
    } catch (error) {
      console.error('Error fetching quotation for partial complete:', error);
      Swal.fire('Error', 'Failed to load quotation items', 'error');
    }
  };

  const handlePartialItemsSubmit = () => {
    if (selectedPartialItems.length === 0) {
      Swal.fire('Error', 'Please select at least one item', 'error');
      return;
    }

    Swal.fire({
      title: 'Partial Completion Details',
      html: `
        <div class="text-left">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Partial Completion Date:</label>
            <input type="date" id="partialDate" class="w-full p-2 border border-gray-300 rounded-md" required />
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Partial Remarks:</label>
            <textarea id="partialRemarks" class="w-full p-2 border border-gray-300 rounded-md h-24" placeholder="Enter partial remarks"></textarea>
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Next Follow-up Date (optional):</label>
            <input type="date" id="nextFollowUpDate" class="w-full p-2 border border-gray-300 rounded-md" />
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Next Follow-up Remarks:</label>
            <textarea id="nextFollowUpRemarks" class="w-full p-2 border border-gray-300 rounded-md h-20" placeholder="Notes for the next follow-up"></textarea>
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Selected Items (${selectedPartialItems.length}):</label>
            <div class="max-h-32 overflow-y-auto bg-gray-50 p-2 rounded border text-xs">
              ${selectedPartialItems.map(item => `
                <div class="flex justify-between py-1 border-b border-gray-200 last:border-b-0">
                  <span>${item.name}</span>
                  <span>Qty: ${item.quantity}</span>
                </div>
              `).join('')}
            </div>
          </div>
        </div>`,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Mark Partial',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#f59e0b',
      preConfirm: () => {
        const partialDate = document.getElementById('partialDate').value;
        const partialRemarks = document.getElementById('partialRemarks').value;
        if (!partialDate) {
          Swal.showValidationMessage('Please enter partial completion date');
          return false;
        }
        if (!partialRemarks || !partialRemarks.trim()) {
          Swal.showValidationMessage('Please enter partial remarks');
          return false;
        }
        const allItems = [];
        (partialItemsData?.sections || []).forEach((section, si) => {
          (section.items || []).forEach((item, ii) => {
            allItems.push({
              key: `${si}-${ii}`,
              name: item.item?.skuDescription || item.itemName || item.customName || 'Unknown Item',
              quantity: item.quantity || 0,
              section: section.name || `Section ${si + 1}`,
            });
          });
        });
        return {
          partialItems: selectedPartialItems,
          allItems,
          selectedKeys: selectedPartialItems.map(item => `${item.sectionIndex}-${item.itemIndex}`),
          partialDate,
          partialRemarks,
          nextFollowUpDate: document.getElementById('nextFollowUpDate').value || null,
          nextFollowUpRemarks: document.getElementById('nextFollowUpRemarks').value,
        };
      },
    }).then(result => {
      if (result.isConfirmed && result.value) {
        submitPartialComplete(partialReminderData, result.value);
        setShowPartialItemsModal(false);
      }
    });
  };

  const handleRefollow = async (id) => {
    const { value: refollowDetails } = await Swal.fire({
      title: 'Enter refollow date and remarks',
      html:
        '<label for="refollow-date" class="block mb-2 text-sm font-medium text-gray-700">Refollow Date</label>' +
        '<input id="refollow-date" type="date" class="block w-full px-4 py-2 mb-3 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500" required>' +
        '<label for="refollow-remarks" class="block mb-2 text-sm font-medium text-gray-700">Refollow Remarks</label>' +
        '<textarea id="refollow-remarks" rows="3" class="block w-full px-4 py-2 mb-3 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500" placeholder="Enter remarks"></textarea>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Refollow',
      preConfirm: () => {
        const refollowDate = document.getElementById('refollow-date').value;
        const refollowRemarks = document.getElementById('refollow-remarks').value;
        if (!refollowDate) {
          Swal.showValidationMessage('Refollow date is required');
        } else if (!refollowRemarks || !refollowRemarks.trim()) {
          Swal.showValidationMessage('Refollow remarks are required');
        }
        return { refollowDate, refollowRemarks };
      },
    });

    if (refollowDetails) {
      try {
        await api.put(`${process.env.REACT_APP_API_BASE_URL}/reminders/refollow/${id}`, {
          refollowDate: refollowDetails.refollowDate,
          refollowRemarks: refollowDetails.refollowRemarks,
          message: `Refollowed on ${refollowDetails.refollowDate} with remarks: ${refollowDetails.refollowRemarks}`,
          date: new Date(refollowDetails.refollowDate),
          refollowedBy: user?.user?._id || user?._id,
          refollowReason: refollowDetails.refollowRemarks || 'Follow-up scheduled'
        });
        Swal.fire('Success', 'Reminder refollowed successfully', 'success');
        fetchReminders();
      } catch (error) {
        console.error('Refollow error:', error);
        Swal.fire('Error', 'Failed to refollow reminder', 'error');
      }
    }
  };

  const handleClosePartialComplete = async (id) => {
    const { value: closeDetails } = await Swal.fire({
      title: 'Close Partial Complete Reminder',
      html:
        '<label for="close-reason" class="block mb-2 text-sm font-medium text-gray-700">Close Reason</label>' +
        '<textarea id="close-reason" rows="3" class="block w-full px-4 py-2 mb-3 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500" placeholder="Enter close reason"></textarea>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Close',
      preConfirm: () => {
        const closeReason = document.getElementById('close-reason').value;
        if (!closeReason || !closeReason.trim()) {
          Swal.showValidationMessage('Please enter a close reason');
          return false;
        }
        return { closeReason };
      },
    });

    if (closeDetails) {
      try {
        await api.put(`${process.env.REACT_APP_API_BASE_URL}/reminders/close-partial/${id}`, {
          closeReason: closeDetails.closeReason || 'Partial completion closed',
          message: `Closed on ${new Date().toLocaleDateString()} with reason: ${closeDetails.closeReason}`,
          closedBy: user.user._id
        });
        Swal.fire('Success', 'Partial complete reminder closed successfully', 'success');
        fetchReminders();
        fetchStats();
      } catch (error) {
        console.error('Close partial complete error:', error);
        Swal.fire('Error', 'Failed to close partial complete reminder', 'error');
      }
    }
  };

  const handleShowHistory = (reminder) => {
    setSelectedReminderHistory(reminder);
    setShowHistoryModal(true);
  };

  const handleReopenReminder = async (reminder) => {
    const { value } = await Swal.fire({
      title: 'Reopen Reminder',
      html:
        '<p class="text-left text-sm text-gray-600 mb-2">This moves the reminder (and its quotation) back to Pending. This can be done only once.</p>' +
        '<label for="reopen-remarks" class="block mb-2 text-sm font-medium text-gray-700 text-left">Remarks *</label>' +
        '<textarea id="reopen-remarks" rows="3" class="block w-full px-4 py-2 mb-3 text-gray-700 bg-white border border-gray-300 rounded-md" placeholder="Reason for reopening"></textarea>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Reopen',
      confirmButtonColor: '#d97706',
      preConfirm: () => {
        const remarks = document.getElementById('reopen-remarks').value;
        if (!remarks || !remarks.trim()) {
          Swal.showValidationMessage('Please enter remarks');
          return false;
        }
        return { remarks };
      },
    });

    if (value) {
      try {
        await api.put(`${process.env.REACT_APP_API_BASE_URL}/reminders/reopen/${reminder._id}`, {
          remarks: value.remarks,
        });
        Swal.fire('Success', 'Reminder reopened successfully', 'success');
        fetchReminders();
        fetchStats();
      } catch (error) {
        Swal.fire('Error', error.response?.data?.error || 'Failed to reopen reminder', 'error');
      }
    }
  };

  const handleFieldVisitAction = async (reminder, outcome) => {
    // outcome: 'VISITED' or 'UNVISITED'
    const label = outcome === 'VISITED' ? 'Visited' : 'Unvisited';
    const today = new Date().toISOString().split('T')[0];
    const { value: details, isConfirmed } = await Swal.fire({
      title: `Mark as ${label}`,
      html: `
        <div class="text-left">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Date:</label>
            <input type="date" id="fv-date" class="w-full p-2 border border-gray-300 rounded-md" value="${today}" required />
          </div>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">Notes:</label>
            <textarea id="fv-notes" class="w-full p-2 border border-gray-300 rounded-md h-20" placeholder="Enter notes"></textarea>
          </div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: label,
      confirmButtonColor: outcome === 'VISITED' ? '#0d9488' : '#6b7280',
      focusConfirm: false,
      preConfirm: () => {
        const date = document.getElementById('fv-date').value;
        if (!date) { Swal.showValidationMessage('Date is required'); return; }
        return { date, notes: document.getElementById('fv-notes').value };
      },
    });
    if (!isConfirmed) return;
    try {
      await handleStatusUpdate(reminder._id, outcome, { date: details.date, notes: details.notes });
      // Prompt to set visit response on the linked FieldVisit
      const { value: response } = await Swal.fire({
        title: 'Set Visit Response',
        html: `
          <p style="margin-bottom:12px;color:#6b7280;font-size:14px">Update the field visit outcome:</p>
          <select id="fv-response-select" style="width:100%;padding:10px 12px;border:1px solid #d1d5db;border-radius:8px;font-size:15px;background:#fff;color:#111827">
            <option value="Positive">✅ Positive</option>
            <option value="Negative">❌ Negative</option>
            <option value="No Response">🔕 No Response</option>
          </select>`,
        showCancelButton: true,
        confirmButtonText: 'Save Response',
        focusConfirm: false,
        preConfirm: () => document.getElementById('fv-response-select').value,
      });
      if (response && reminder.dataid) {
        await api.put(`${process.env.REACT_APP_API_BASE_URL}/field-visits/${reminder.dataid}/status`, { status: response });
        Swal.fire('Done', `Visit response set to ${response}`, 'success');
      }
    } catch (err) {
      console.error('Field visit action error:', err);
    }
  };

  const handleFieldVisitReschedule = async (reminder) => {
    const today = new Date().toISOString().split('T')[0];
    const { value: refollowDetails, isConfirmed } = await Swal.fire({
      title: '🔁 Reschedule Visit',
      html:
        '<label for="fvr-date" class="block mb-2 text-sm font-medium text-gray-700">Next Visit Date</label>' +
        `<input id="fvr-date" type="date" min="${today}" class="block w-full px-4 py-2 mb-3 text-gray-700 bg-white border border-gray-300 rounded-md" required>` +
        '<label for="fvr-remarks" class="block mb-2 text-sm font-medium text-gray-700">Remarks</label>' +
        '<textarea id="fvr-remarks" rows="3" class="block w-full px-4 py-2 mb-3 text-gray-700 bg-white border border-gray-300 rounded-md" placeholder="Enter remarks"></textarea>',
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: 'Reschedule',
      preConfirm: () => {
        const date = document.getElementById('fvr-date').value;
        if (!date) { Swal.showValidationMessage('Date is required'); return; }
        return { refollowDate: date, refollowRemarks: document.getElementById('fvr-remarks').value };
      },
    });
    if (!isConfirmed || !refollowDetails) return;
    try {
      await api.put(`${process.env.REACT_APP_API_BASE_URL}/reminders/refollow/${reminder._id}`, {
        refollowDate: refollowDetails.refollowDate,
        refollowRemarks: refollowDetails.refollowRemarks,
        message: `Rescheduled to ${refollowDetails.refollowDate}: ${refollowDetails.refollowRemarks}`,
        date: new Date(refollowDetails.refollowDate),
        refollowedBy: user?.user?._id,
        refollowReason: refollowDetails.refollowRemarks || 'Visit rescheduled',
      });
      Swal.fire('Success', 'Visit rescheduled', 'success');
      fetchReminders();
    } catch (err) {
      console.error('Field visit reschedule error:', err);
      Swal.fire('Error', 'Failed to reschedule', 'error');
    }
  };

  const getRecipientName = (reminder) => {
    // Try multiple paths to get recipient name
    if (reminder.recipient) {
      return reminder.recipient.name || 
             reminder.recipient.firmName || 
             reminder.recipient.businessName || 
             reminder.recipient.Name || 
             reminder.recipient.mailingName;
    }
    
    if (reminder.dataid) {
      return reminder.dataid.name || 
             reminder.dataid.firmName || 
             reminder.dataid.businessName || 
             reminder.dataid.Name || 
             reminder.dataid.mailingName;
    }
    
    return 'Unknown Recipient';
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    if (selectedReminders.length === 0) {
      Swal.fire('Warning', 'Please select reminders to update', 'warning');
      return;
    }

    const result = await Swal.fire({
      title: `Update ${selectedReminders.length} reminders to ${newStatus}?`,
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, update',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await Promise.all(
          selectedReminders.map(id =>
            api.put(`${process.env.REACT_APP_API_BASE_URL}/reminders/status/${id}`, {
              status: newStatus,
              details: { bulkUpdate: true, updatedAt: new Date() }
            })
          )
        );
        
        Swal.fire('Success', `${selectedReminders.length} reminders updated successfully`, 'success');
        setSelectedReminders([]);
        fetchReminders();
        fetchStats();
      } catch (error) {
        Swal.fire('Error', 'Failed to update some reminders', 'error');
      }
    }
  };

  const toggleReminderSelection = (reminderId) => {
    setSelectedReminders(prev => 
      prev.includes(reminderId) 
        ? prev.filter(id => id !== reminderId)
        : [...prev, reminderId]
    );
  };

  const selectAllReminders = () => {
    if (selectedReminders.length === reminders.length) {
      setSelectedReminders([]);
    } else {
      setSelectedReminders(reminders.map(r => r._id));
    }
  };

  const formatRelativeTime = (date) => {
    const now = new Date();
    const reminderDate = new Date(date);
    const diffInMs = reminderDate - now;
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMs < 0) {
      const pastDays = Math.abs(diffInDays);
      if (pastDays === 0) {
        return 'Today';
      } else if (pastDays === 1) {
        return '1 day ago';
      } else {
        return `${pastDays} days ago`;
      }
    } else if (diffInDays === 0) {
      if (diffInHours <= 0) {
        return 'Due now';
      } else if (diffInHours === 1) {
        return 'Due in 1 hour';
      } else {
        return `Due in ${diffInHours} hours`;
      }
    } else if (diffInDays === 1) {
      return 'Due tomorrow';
    } else if (diffInDays <= 7) {
      return `Due in ${diffInDays} days`;
    } else {
      return `Due in ${Math.ceil(diffInDays / 7)} weeks`;
    }
  };

  const handleFormSubmission = (id, newStatus, details) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to update the status of this reminder.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, update status!',
    }).then((result) => {
      if (result.isConfirmed) {
        handleStatusUpdate(id, newStatus, details);
      }
    });
  };

  useEffect(() => {
    setShowBulkActions(selectedReminders.length > 0);
  }, [selectedReminders]);

  const totalPages = Math.ceil(totalReminders / limit);
  
  const getStatusBadgeColor = (status) => {
    if (isDarkMode) {
      switch (status) {
        case 'COMPLETED': return 'bg-green-900/40 text-green-300 border-green-700';
        case 'CANCELLED': return 'bg-red-900/40 text-red-300 border-red-700';
        case 'NEXT-FOLLOW-UP': return 'bg-blue-900/40 text-blue-300 border-blue-700';
        case 'PARTIAL-COMPLETE': return 'bg-orange-900/40 text-orange-300 border-orange-700';
        case 'PARTIALLY-CLOSED': return 'bg-amber-900/40 text-amber-300 border-amber-700';
        case 'PENDING': return 'bg-yellow-900/40 text-yellow-300 border-yellow-700';
        case 'VISITED': return 'bg-teal-900/40 text-teal-300 border-teal-700';
        case 'UNVISITED': return 'bg-gray-700 text-gray-300 border-gray-600';
        default: return 'bg-gray-700 text-gray-300 border-gray-600';
      }
    }
    switch (status) {
      case 'COMPLETED': return 'bg-green-100 text-green-800 border-green-200';
      case 'CANCELLED': return 'bg-red-100 text-red-800 border-red-200';
      case 'NEXT-FOLLOW-UP': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PARTIAL-COMPLETE': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'PARTIALLY-CLOSED': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'VISITED': return 'bg-teal-100 text-teal-800 border-teal-200';
      case 'UNVISITED': return 'bg-gray-100 text-gray-700 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryBadgeColor = (category) => {
    if (isDarkMode) {
      switch (category) {
        case 'SALES': return 'bg-purple-900/40 text-purple-300 border-purple-700';
        case 'PURCHASE': return 'bg-indigo-900/40 text-indigo-300 border-indigo-700';
        case 'QUOTATION':
        case 'QUOTATION_NEW': return 'bg-blue-900/40 text-blue-300 border-blue-700';
        case 'PAYMENT': return 'bg-green-900/40 text-green-300 border-green-700';
        case 'TASK': return 'bg-orange-900/40 text-orange-300 border-orange-700';
        default: return 'bg-gray-700 text-gray-300 border-gray-600';
      }
    }
    switch (category) {
      case 'SALES': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'PURCHASE': return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'QUOTATION':
      case 'QUOTATION_NEW': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'PAYMENT': return 'bg-green-100 text-green-800 border-green-200';
      case 'TASK': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'FIELD_VISIT': return isDarkMode ? 'bg-cyan-900/40 text-cyan-300 border-cyan-700' : 'bg-cyan-100 text-cyan-800 border-cyan-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className={`p-6 min-h-screen ${isDarkMode ? 'bg-gray-900 rem-dark-page' : 'bg-gray-50'}`}>
      <div className="w-full mx-auto">
        {/* Sticky toolbar: header + search + clickable status chips */}
        <div className={`sticky top-0 z-30 -mx-6 px-6 pt-6 pb-3 -mt-6 mb-4 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
          <div className={`rounded-lg shadow-sm border p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div>
                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Reminders</h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Manage your reminders and follow-ups</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
                <SearchInput
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                  onClear={() => { setSearchQuery(''); setPage(0); }}
                  placeholder="Search reminders…"
                  isDark={isDarkMode}
                  className="flex-1 sm:w-64"
                />
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${
                    showFilters
                      ? 'bg-blue-600 text-white'
                      : isDarkMode ? 'bg-gray-700 border border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <i className="fas fa-filter mr-1"></i>
                  {showFilters ? 'Hide Filters' : 'Filters'}
                </button>
                <button
                  onClick={clearFilters}
                  className={`px-3 py-2 rounded-lg text-sm transition-colors ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  <i className="fas fa-times mr-1"></i>Clear
                </button>
                <button
                  onClick={() => fetchReminders()}
                  disabled={loading}
                  className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 text-sm"
                >
                  <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-sync-alt'} mr-1`}></i>Refresh
                </button>
              </div>
            </div>

            {/* Clickable status chips (click to filter) */}
            <div className="flex flex-wrap gap-2 mt-3">
              {[
                { label: 'Total', value: stats.total, status: '' },
                { label: 'Pending', value: stats.pending, status: 'PENDING' },
                { label: 'Follow-up', value: stats.followUp, status: 'NEXT-FOLLOW-UP' },
                { label: 'Completed', value: stats.completed, status: 'COMPLETED' },
                { label: 'Partial Complete', value: stats.partialComplete, status: 'PARTIAL-COMPLETE' },
                { label: 'Partially Closed', value: stats.partiallyClosed, status: 'PARTIALLY-CLOSED' },
                { label: 'Cancelled', value: stats.cancelled, status: 'CANCELLED' },
              ].map((chip) => {
                const active = status === chip.status;
                return (
                  <button
                    key={chip.label}
                    onClick={() => { setStatus(active ? '' : chip.status); setPage(0); }}
                    className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                      active
                        ? 'bg-blue-600 text-white border-blue-600'
                        : isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {chip.label}: <span className="font-bold">{chip.value ?? 0}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Reminder Charts */}
        {stats.total > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <ChartCard title="Status Breakdown" isDark={isDarkMode}>
              <LazyMount style={{ height: '250px' }}>
                <EChart option={pieOption({
                  data: [
                    { name: 'Pending', value: stats.pending, color: '#F59E0B' },
                    { name: 'Completed', value: stats.completed, color: '#22C55E' },
                    { name: 'Follow-up', value: stats.followUp, color: '#8B5CF6' },
                    { name: 'Partial', value: stats.partialComplete, color: '#0EA5E9' },
                    { name: 'Partially Closed', value: stats.partiallyClosed, color: '#6366F1' },
                    { name: 'Cancelled', value: stats.cancelled, color: '#EF4444' },
                  ],
                  isDark: isDarkMode,
                  donut: true,
                  centerLabel: 'Reminders',
                })} />
              </LazyMount>
            </ChartCard>
            <ChartCard title="Urgency (Pending by Due Date)" isDark={isDarkMode}>
              <LazyMount style={{ height: '250px' }}>
                <EChart option={barOption({
                  labels: ['Overdue', 'Due Today', 'This Week'],
                  series: [{
                    name: 'Pending',
                    data: [
                      { value: stats.overdue || 0, itemStyle: { color: '#EF4444' } },
                      { value: stats.dueToday || 0, itemStyle: { color: '#F59E0B' } },
                      { value: stats.dueThisWeek || 0, itemStyle: { color: '#3B82F6' } },
                    ],
                  }],
                  isDark: isDarkMode,
                })} />
              </LazyMount>
            </ChartCard>
            <TrendCard
              title="Completed (Last 7 Days)"
              labels={(stats.completedTrend || []).map(d => d.label)}
              data={(stats.completedTrend || []).map(d => d.count)}
              color={isDarkMode ? '#34D399' : '#16A34A'}
              isDark={isDarkMode}
              emptyMessage="No completions in the last 7 days"
            />
          </div>
        )}

        {/* Filters */}
        {showFilters && (
          <div className="bg-white rounded-lg shadow-sm border mb-6 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* Date Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="PENDING">Pending</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                  <option value="NEXT-FOLLOW-UP">Next Follow-up</option>
                  <option value="PARTIAL-COMPLETE">Partial Complete</option>
                  <option value="PARTIALLY-CLOSED">Partially Closed</option>
                  <option value="VISITED">Visited</option>
                  <option value="UNVISITED">Unvisited</option>
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Categories</option>
                  <option value="SALES">Sales</option>
                  <option value="PURCHASE">Purchase</option>
                  <option value="QUOTATION">Quotation</option>
                  <option value="QUOTATION_NEW">New Quotation</option>
                  <option value="PAYMENT">Payment</option>
                  <option value="NEXT-FOLLOW-UP">Next Follow-up</option>
                  <option value="TASK">Task</option>
                  <option value="FIELD_VISIT">Field Visit</option>
                </select>
              </div>

              {/* Data Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data Type</label>
                <select
                  value={datatype}
                  onChange={(e) => setDatatype(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Types</option>
                  <option value="TeleCallingPayment">Tele Calling Payment</option>
                  <option value="Quotation">Quotation</option>
                  <option value="QuotationNew">New Quotation</option>
                  <option value="TallyQuotation">Tally Quotation</option>
                  <option value="ActivityLogNew">Activity Log</option>
                  <option value="ProformaInvoice">Proforma Invoice</option>
                  <option value="FieldVisit">Field Visit</option>
                </select>
              </div>

              {/* Salesperson Filter - Only show for QuotationNew and TallyQuotation */}
              {(datatype === 'QuotationNew' || datatype === 'TallyQuotation') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <i className="fas fa-user-tie mr-1"></i>
                    Salesperson Filter
                  </label>
                  <Select
                    isMulti
                    options={availableSalesmenFilter.map(emp => ({
                      value: emp._id,
                      label: emp.name
                    }))}
                    value={selectedSalesmanFilter.map(id => ({
                      value: id,
                      label: availableSalesmenFilter.find(emp => emp._id === id)?.name || ''
                    }))}
                    onChange={(selected) => {
                      setSelectedSalesmanFilter(selected ? selected.map(option => option.value) : []);
                    }}
                    className="w-full"
                    placeholder="All Salesperson"
                    isClearable
                    isSearchable
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        minHeight: '48px',
                        border: isDarkMode ? '1px solid #4b5563' : '1px solid #d1d5db',
                        borderRadius: '0.5rem',
                        boxShadow: 'none',
                        backgroundColor: isDarkMode ? '#374151' : provided.backgroundColor,
                        '&:hover': {
                          borderColor: isDarkMode ? '#6b7280' : '#d1d5db'
                        },
                        '&:focus-within': {
                          borderColor: '#3b82f6',
                          outline: '2px solid #3b82f6',
                          outlineOffset: '2px'
                        }
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? (isDarkMode ? '#374151' : '#eff6ff') : (isDarkMode ? '#1f2937' : 'white'),
                        color: state.isSelected ? 'white' : (isDarkMode ? '#f3f4f6' : '#374151')
                      }),
                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: isDarkMode ? '#1f2937' : provided.backgroundColor,
                        border: isDarkMode ? '1px solid #4b5563' : provided.border
                      }),
                      multiValue: (provided) => ({
                        ...provided,
                        backgroundColor: isDarkMode ? '#4b5563' : provided.backgroundColor
                      }),
                      multiValueLabel: (provided) => ({
                        ...provided,
                        color: isDarkMode ? '#f3f4f6' : provided.color
                      }),
                      input: (provided) => ({
                        ...provided,
                        color: isDarkMode ? '#f3f4f6' : provided.color
                      })
                    }}
                  />
                  {availableSalesmenFilter.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      <i className="fas fa-info-circle mr-1"></i>
                      Loading salesmen...
                    </p>
                  )}
                </div>
              )}

              {/* Sort Options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="date">Date</option>
                  <option value="status">Status</option>
                  <option value="category">Category</option>
                  <option value="createdAt">Created Date</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>

              {/* Items per page */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Items per page</label>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(parseInt(e.target.value));
                    setPage(0);
                  }}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

          </div>
        )}

        {/* Status chips moved into the sticky toolbar above */}
        {/* Bulk Actions */}
        {showBulkActions && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <i className="fas fa-check-square text-blue-600"></i>
                <span className="font-medium text-blue-900">
                  {selectedReminders.length} reminder{selectedReminders.length !== 1 ? 's' : ''} - {
                    selectedReminders.length > 0 
                      ? reminders
                          .filter(r => selectedReminders.includes(r._id))
                          .map(r => getRecipientName(r))
                          .join(', ')
                      : 'recipient name'
                  }
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {(() => {
                  const selectedObjs = reminders.filter(r => selectedReminders.includes(r._id));
                  const allFieldVisit = selectedObjs.length > 0 && selectedObjs.every(r => r.category === 'FIELD_VISIT');
                  const hasFieldVisit = selectedObjs.some(r => r.category === 'FIELD_VISIT');
                  return (
                    <>
                      {allFieldVisit ? (
                        <>
                          <button
                            onClick={() => handleBulkStatusUpdate('VISITED')}
                            className="px-3 py-2 bg-teal-600 text-white rounded-lg text-sm hover:bg-teal-700 transition-colors"
                          >
                            <i className="fas fa-check mr-2"></i>
                            Mark Visited
                          </button>
                          <button
                            onClick={() => handleBulkStatusUpdate('UNVISITED')}
                            className="px-3 py-2 bg-gray-500 text-white rounded-lg text-sm hover:bg-gray-600 transition-colors"
                          >
                            <i className="fas fa-times mr-2"></i>
                            Mark Unvisited
                          </button>
                        </>
                      ) : !hasFieldVisit ? (
                        <>
                          <button
                            onClick={() => handleBulkStatusUpdate('COMPLETED')}
                            className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 transition-colors"
                          >
                            <i className="fas fa-check mr-2"></i>
                            Mark Completed
                          </button>
                          <button
                            onClick={() => handleBulkStatusUpdate('PARTIAL-COMPLETE')}
                            className="px-3 py-2 bg-orange-600 text-white rounded-lg text-sm hover:bg-orange-700 transition-colors"
                          >
                            <i className="fas fa-clock mr-2"></i>
                            Mark Partial
                          </button>
                        </>
                      ) : null}
                      <button
                        onClick={() => handleBulkStatusUpdate('CANCELLED')}
                        className="px-3 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition-colors"
                      >
                        <i className="fas fa-times mr-2"></i>
                        Cancel All
                      </button>
                      <button
                        onClick={() => setSelectedReminders([])}
                        className="px-3 py-2 bg-gray-600 text-white rounded-lg text-sm hover:bg-gray-700 transition-colors"
                      >
                        <i className="fas fa-times-circle mr-2"></i>
                        Clear Selection
                      </button>
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Reminders List */}
        <div className="bg-white rounded-lg shadow-sm border">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="text-center">
                <i className="fas fa-spinner fa-spin text-3xl text-blue-600 mb-4"></i>
                <p className="text-gray-600">Loading reminders...</p>
              </div>
            </div>
          ) : reminders.length === 0 ? (
            <div className="text-center py-12">
              <i className="fas fa-bell-slash text-5xl text-gray-400 mb-4"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No reminders found</h3>
              <p className="text-gray-600">Try adjusting your filters or create a new reminder.</p>
            </div>
          ) : (
            <div>
              {/* Select All Header */}
              <div className="border-b border-gray-200 px-6 py-4 bg-gray-50">
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedReminders.length === reminders.length && reminders.length > 0}
                      onChange={selectAllReminders}
                      className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Select All ({reminders.length})
                    </span>
                  </label>
                  <div className="text-sm text-gray-600">
                    {selectedReminders.length > 0 && (
                      `${selectedReminders.length} reminders: ${
                        reminders
                          .filter(r => selectedReminders.includes(r._id))
                          .map(r => getRecipientName(r))
                          .join(', ')
                      }`
                    )}
                  </div>
                </div>
              </div>
              
              {/* Reminders */}
              <div className="divide-y divide-gray-200">
                {reminders.map((reminder) => {
                  const isSelected = selectedReminders.includes(reminder._id);
                  const recipientName = getRecipientName(reminder);
                  const hasRecipient = recipientName !== 'Unknown Recipient';
                  const recipientPhone = reminder.recipient?.phone || reminder.dataid?.phone;
                  const recipientEmail = reminder.recipient?.email || reminder.dataid?.email;
                  const recipientStation = reminder.recipient?.station?.name || reminder.dataid?.station?.name;
                  const recipientAddress = reminder.recipient?.address || reminder.dataid?.address;
                  const recipientCity = reminder.recipient?.city || reminder.dataid?.city;
                  const quotationId = reminder.dataid?.id;
                  const recipientFirm = reminder.recipient?.firmName || reminder.recipient?.businessName || reminder.dataid?.firmName || reminder.dataid?.businessName;
                  const recipientGST = reminder.recipient?.gstNo || reminder.dataid?.GSTIN;
                  
                  return (
                    <div
                      key={reminder._id}
                      className={`p-3 sm:p-4 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                    >
                      <div className="flex items-start gap-3">
                        {/* Checkbox */}
                        <div className="flex-shrink-0 pt-1">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleReminderSelection(reminder._id)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                        </div>
                        
                        {/* Main Content */}
                        <div className="flex-1 min-w-0">
                          {/* Row 1: Recipient name + badges */}
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="text-base font-semibold text-gray-900">
                              {hasRecipient ? recipientName : reminder.category?.replace('_', ' ')}
                            </h3>
                            {recipientFirm && recipientFirm !== recipientName && (
                              <span className="text-sm text-gray-500">({recipientFirm})</span>
                            )}
                            {quotationId && (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                                #{quotationId}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getCategoryBadgeColor(reminder.category)}`}>
                              {reminder.category?.replace('_', ' ')}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeColor(reminder.status)}`}>
                              {reminder.status}
                            </span>
                            {reminder.reopened && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200">
                                Reopened
                              </span>
                            )}
                            <span className="px-2 py-0.5 rounded text-xs bg-gray-50 text-gray-500 border border-gray-100">
                              {reminder.datatype}
                            </span>
                          </div>
                          
                          {/* Row 2: Key details inline */}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 mb-2">
                            {recipientPhone && (
                              <span className="flex items-center gap-1">
                                <i className="fas fa-phone text-xs text-gray-400"></i>
                                {recipientPhone}
                              </span>
                            )}
                            {recipientEmail && (
                              <span className="flex items-center gap-1">
                                <i className="fas fa-envelope text-xs text-gray-400"></i>
                                {recipientEmail}
                              </span>
                            )}
                            {(recipientStation || recipientCity) && (
                              <span className="flex items-center gap-1">
                                <i className="fas fa-map-marker-alt text-xs text-gray-400"></i>
                                {recipientStation || recipientCity}
                                {recipientStation && recipientCity && recipientStation !== recipientCity ? `, ${recipientCity}` : ''}
                              </span>
                            )}
                            {recipientGST && (
                              <span className="flex items-center gap-1">
                                <i className="fas fa-file-alt text-xs text-gray-400"></i>
                                GST: {recipientGST}
                              </span>
                            )}
                          </div>

                          {/* Row 3: Message */}
                          {reminder.message && (
                            <p className="text-sm text-gray-700 mb-2 leading-relaxed">{reminder.message}</p>
                          )}

                          {/* Row 3b: Remarks */}
                          {reminder.remarks && (
                            <p className="text-sm text-gray-600 mb-2 leading-relaxed italic">
                              <span className="font-medium not-italic">Remarks:</span> {reminder.remarks}
                            </p>
                          )}
                          
                          {/* Row 4: Date info compact */}
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
                            <span className="flex items-center gap-1">
                              <i className="fas fa-calendar text-gray-400"></i>
                              Due: {new Date(reminder.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} {new Date(reminder.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                            <span className={`font-medium ${new Date(reminder.date) < new Date() ? 'text-red-500' : 'text-green-600'}`}>
                              {formatRelativeTime(reminder.date)}
                            </span>
                            <span>Created: {new Date(reminder.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          </div>

                          {/* Refollow History Button */}
                          {reminder.updatedDetails && reminder.updatedDetails.length > 0 && (
                            <div className="mb-2">
                              <button
                                onClick={() => handleShowHistory(reminder)}
                                className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg text-xs transition-colors border border-amber-300"
                              >
                                <i className="fas fa-history"></i>
                                <span>Follow-up History ({reminder.updatedDetails.length})</span>
                              </button>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
                            {(reminder.category === 'QUOTATION' || reminder.category === 'QUOTATION_NEW' || 
                              ['QuotationNew', 'TallyQuotation'].includes(reminder.datatype)) && (
                              <>
                                <button
                                  onClick={() => handleViewQuotation(reminder)}
                                  disabled={loadingReminders[reminder._id] === 'view'}
                                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-lg text-sm transition-colors flex items-center"
                                >
                                  <i className="fas fa-eye mr-2"></i>
                                  {loadingReminders[reminder._id] === 'view' ? 'Loading...' : 'View Details'}
                                </button>
                                
                                <button
                                  onClick={() => handleExportQuotation(reminder)}
                                  disabled={loadingReminders[reminder._id] === 'export'}
                                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white rounded-lg text-sm transition-colors flex items-center"
                                >
                                  <i className="fas fa-download mr-2"></i>
                                  {loadingReminders[reminder._id] === 'export' ? 'Loading...' : 'Export Excel'}
                                </button>
                              </>
                            )}                        {reminder.category === 'FIELD_VISIT' && (reminder.status === 'PENDING' || reminder.status === 'NEXT-FOLLOW-UP') && (
                          <>
                            <button
                              onClick={() => handleFieldVisitAction(reminder, 'VISITED')}
                              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm transition-colors flex items-center"
                            >
                              <i className="fas fa-check mr-2"></i>
                              Visited
                            </button>
                            <button
                              onClick={() => handleFieldVisitAction(reminder, 'UNVISITED')}
                              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm transition-colors flex items-center"
                            >
                              <i className="fas fa-times mr-2"></i>
                              Unvisited
                            </button>
                            <button
                              onClick={() => handleFieldVisitReschedule(reminder)}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors flex items-center"
                            >
                              <i className="fas fa-redo mr-2"></i>
                              Reschedule
                            </button>
                          </>
                        )}
                        {reminder.category !== 'FIELD_VISIT' && ['PENDING', 'NEXT-FOLLOW-UP', 'PARTIAL-COMPLETE'].includes(reminder.status) && (
                          <>
                            <button
                              onClick={() =>
                                Swal.fire({
                                  title: 'Complete Reminder',
                                  html: `
                                    <div class="text-left">
                                      <div class="mb-4">
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Date of Completion:</label>
                                        <input type="date" id="completionDate" class="w-full p-2 border border-gray-300 rounded-md" required />
                                      </div>
                                      <div class="mb-4">
                                        <label class="block text-sm font-medium text-gray-700 mb-2">Completion Notes:</label>
                                        <textarea id="completionNotes" class="w-full p-2 border border-gray-300 rounded-md h-24" placeholder="Enter completion notes"></textarea>
                                      </div>
                                    </div>`,
                                  focusConfirm: false,
                                  showCancelButton: true,
                                  confirmButtonText: 'Complete',
                                  cancelButtonText: 'Cancel',
                                  confirmButtonColor: '#10b981',
                                  preConfirm: () => {
                                    const completionDate = document.getElementById('completionDate').value;
                                    const completionNotes = document.getElementById('completionNotes').value;
                                    if (!completionDate) {
                                      Swal.showValidationMessage('Please enter completion date');
                                    } else if (!completionNotes || !completionNotes.trim()) {
                                      Swal.showValidationMessage('Please enter completion remarks');
                                    } else {
                                      handleFormSubmission(reminder._id, 'COMPLETED', {
                                        completionDate,
                                        completionNotes,
                                      });
                                    }
                                  },
                                })
                              }
                              className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm transition-colors flex items-center"
                            >
                              <i className="fas fa-check mr-2"></i>
                              Complete
                            </button>

                            <button
                              onClick={() => handlePartialComplete(reminder)}
                              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg text-sm transition-colors flex items-center"
                            >
                              <i className="fas fa-clock mr-2"></i>
                              Partial
                            </button>

                            {(reminder.status === 'PENDING' || reminder.status === 'NEXT-FOLLOW-UP') && (
                            <button
                              onClick={() =>
                                Swal.fire({
                                  title: 'Cancel Reminder',
                                  input: 'textarea',
                                  inputLabel: 'Reason for cancelling',
                                  inputPlaceholder: 'Enter reason for cancellation...',
                                  showCancelButton: true,
                                  confirmButtonText: 'Cancel Reminder',
                                  cancelButtonText: 'Keep Reminder',
                                  confirmButtonColor: '#ef4444',
                                  preConfirm: (reason) => {
                                    if (!reason) {
                                      Swal.showValidationMessage('Please enter a reason');
                                    }
                                    return reason;
                                  },
                                }).then((result) => {
                                  if (result.isConfirmed) {
                                    handleFormSubmission(reminder._id, 'CANCELLED', { reason: result.value });
                                  }
                                })
                              }
                              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors flex items-center"
                            >
                              <i className="fas fa-times mr-2"></i>
                              Cancel
                            </button>
                            )}
                          </>
                        )}

                        {reminder.status === 'PARTIAL-COMPLETE' && (
                          <button
                            onClick={() => handleClosePartialComplete(reminder._id)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors flex items-center"
                          >
                            <i className="fas fa-check-circle mr-2"></i>
                            Close
                          </button>
                        )}

                        {Array.isArray(reminder.details?.items) && reminder.details.items.length > 0 &&
                         ['PARTIAL-COMPLETE', 'PARTIALLY-CLOSED'].includes(reminder.status) && (
                          <button
                            onClick={() => { setSelectedItemsReminder(reminder); setShowItemsModal(true); }}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors flex items-center"
                          >
                            <i className="fas fa-eye mr-2"></i>
                            Items
                          </button>
                        )}

                        {['PENDING', 'NEXT-FOLLOW-UP', 'PARTIAL-COMPLETE'].includes(reminder.status) && reminder.category !== 'FIELD_VISIT' && (
                          <button
                            onClick={() => handleRefollow(reminder._id)}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm transition-colors flex items-center"
                          >
                            <i className="fas fa-redo mr-2"></i>
                            Refollow
                          </button>
                        )}

                        {['COMPLETED', 'CANCELLED'].includes(reminder.status) && !reminder.reopened && (
                          <button
                            onClick={() => handleReopenReminder(reminder)}
                            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm transition-colors flex items-center"
                          >
                            <i className="fas fa-undo mr-2"></i>
                            Reopen
                          </button>
                        )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalReminders > 0 && (
            <div className="bg-gray-50 px-6 py-4 border-t">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="text-sm text-gray-700">
                  Showing {page * limit + 1} to {Math.min((page + 1) * limit, totalReminders)} of {totalReminders} results
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="fas fa-chevron-left mr-1"></i>
                    Previous
                  </button>
                  
                  <div className="hidden sm:flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                      let pageNumber;
                      if (totalPages <= 5) {
                        pageNumber = index;
                      } else if (page < 3) {
                        pageNumber = index;
                      } else if (page > totalPages - 4) {
                        pageNumber = totalPages - 5 + index;
                      } else {
                        pageNumber = page - 2 + index;
                      }
                      
                      return (
                        <button
                          key={pageNumber}
                          onClick={() => setPage(pageNumber)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            pageNumber === page
                              ? 'bg-blue-600 text-white'
                              : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber + 1}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page === totalPages - 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <i className="fas fa-chevron-right ml-1"></i>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

      {/* Quotation Details Modal */}
      {showQuotationModal && selectedQuotation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-xl md:text-2xl font-bold flex items-center">
                  <i className="fas fa-file-invoice mr-3"></i>
                  {quotationType === 'QuotationNew' ? 'New Quotation' : 
                   quotationType === 'TallyQuotation' ? 'Tally Quotation' : 'Quotation'} Details
                </h3>
                <p className="text-blue-100 text-sm md:text-base mt-1">
                  <i className="fas fa-hashtag mr-1"></i>
                  ID: {selectedQuotation.id}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleExportQuotation({ datatype: quotationType, dataid: { _id: selectedQuotation._id } })}
                  disabled={loadingReminders[selectedQuotation._id] === 'export'}
                  className="px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg text-sm transition-colors flex items-center"
                >
                  <i className="fas fa-download mr-2"></i>
                  {loadingReminders[selectedQuotation._id] === 'export' ? 'Loading...' : 'Export Excel'}
                </button>
                <button
                  onClick={() => setShowQuotationModal(false)}
                  className="text-white hover:text-gray-200 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-white hover:bg-opacity-20 transition-colors"
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Status Banner */}
              <div className={`p-4 rounded-lg border-l-4 mb-6 ${
                selectedQuotation.status === 'PENDING' ? 'bg-yellow-50 border-yellow-400' :
                selectedQuotation.status === 'APPROVED' ? 'bg-green-50 border-green-400' :
                selectedQuotation.status === 'REFFOLLOW' ? 'bg-blue-50 border-blue-400' :
                'bg-red-50 border-red-400'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <i className={`fas fa-circle mr-2 ${
                      selectedQuotation.status === 'PENDING' ? 'text-yellow-500' :
                      selectedQuotation.status === 'APPROVED' ? 'text-green-500' :
                      selectedQuotation.status === 'REFFOLLOW' ? 'text-blue-500' :
                      'text-red-500'
                    }`}></i>
                    <span className="font-semibold text-gray-800">
                      Status: {selectedQuotation.status}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <i className="fas fa-calendar mr-1"></i>
                    Created: {new Date(selectedQuotation.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Basic Information Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                {/* Quotation Information */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                  <h4 className="font-bold text-blue-900 mb-4 flex items-center text-lg">
                    <i className="fas fa-file-alt mr-2"></i>
                    Quotation Information
                  </h4>
                  <div className="space-y-3">
                    {selectedQuotation.validTill && (
                      <div className="flex items-center">
                        <i className="fas fa-clock text-blue-600 mr-2 w-4"></i>
                        <span className="font-medium text-gray-700">Valid Till:</span>
                        <span className="ml-2 text-gray-900">{new Date(selectedQuotation.validTill).toLocaleDateString()}</span>
                      </div>
                    )}
                    {selectedQuotation.pricingType && (
                      <div className="flex items-center">
                        <i className="fas fa-tags text-blue-600 mr-2 w-4"></i>
                        <span className="font-medium text-gray-700">Pricing Type:</span>
                        <span className="ml-2 text-gray-900">{selectedQuotation.pricingType}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <i className="fas fa-calendar-plus text-blue-600 mr-2 w-4"></i>
                      <span className="font-medium text-gray-700">Created:</span>
                      <span className="ml-2 text-gray-900">{new Date(selectedQuotation.createdAt).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                
                {/* Customer Information */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                  <h4 className="font-bold text-green-900 mb-4 flex items-center text-lg">
                    <i className="fas fa-user-circle mr-2"></i>
                    Customer Information
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-start">
                      <i className="fas fa-user text-green-600 mr-2 w-4 mt-1"></i>
                      <div>
                        <span className="font-medium text-gray-700">Name:</span>
                        <div className="font-semibold text-gray-900 text-lg">
                          {selectedQuotation.recipient?.name || selectedQuotation.recipient?.firmName || 'N/A'}
                        </div>
                      </div>
                    </div>
                    
                    {selectedQuotation.recipient?.email && (
                      <div className="flex items-center">
                        <i className="fas fa-envelope text-green-600 mr-2 w-4"></i>
                        <span className="font-medium text-gray-700">Email:</span>
                        <a href={`mailto:${selectedQuotation.recipient.email}`} 
                           className="ml-2 text-blue-600 hover:text-blue-800 underline">
                          {selectedQuotation.recipient.email}
                        </a>
                      </div>
                    )}
                    
                    {selectedQuotation.recipient?.phone && (
                      <div className="flex items-center">
                        <i className="fas fa-phone text-green-600 mr-2 w-4"></i>
                        <span className="font-medium text-gray-700">Phone:</span>
                        <a href={`tel:${selectedQuotation.recipient.phone}`}
                           className="ml-2 text-blue-600 hover:text-blue-800 underline">
                          {selectedQuotation.recipient.phone}
                        </a>
                      </div>
                    )}
                    
                    {selectedQuotation.recipient?.station?.name && (
                      <div className="flex items-center">
                        <i className="fas fa-map-marker-alt text-green-600 mr-2 w-4"></i>
                        <span className="font-medium text-gray-700">Station:</span>
                        <span className="ml-2 text-gray-900">{selectedQuotation.recipient.station.name}</span>
                      </div>
                    )}
                    
                    {selectedQuotation.siteAddress && (
                      <div className="flex items-start">
                        <i className="fas fa-building text-green-600 mr-2 w-4 mt-1"></i>
                        <div>
                          <span className="font-medium text-gray-700">Site Address:</span>
                          <div className="text-gray-900 break-words">{selectedQuotation.siteAddress}</div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      <i className="fas fa-tag text-green-600 mr-2 w-4"></i>
                      <span className="font-medium text-gray-700">Type:</span>
                      <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        {selectedQuotation.recipientType || 'Customer'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Salesperson Information */}
              {((selectedQuotation.salesmen && selectedQuotation.salesmen.length > 0) || 
                (selectedQuotation.salesman && selectedQuotation.salesman.employee)) && (
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200 mb-6">
                  <h4 className="font-bold text-purple-900 mb-4 flex items-center text-lg">
                    <i className="fas fa-users mr-2"></i>
                    Sales Team
                  </h4>
                  <div className="flex flex-wrap gap-3">
                    {selectedQuotation.salesmen ? selectedQuotation.salesmen.map((salesman, index) => (
                      <div key={index} className="bg-white px-4 py-2 rounded-full border border-purple-200 flex items-center">
                        <i className="fas fa-user-tie text-purple-600 mr-2"></i>
                        <span className="font-medium text-purple-900">
                          {salesman.name || salesman}
                        </span>
                      </div>
                    )) : selectedQuotation.salesman && (
                      <div className="bg-white px-4 py-2 rounded-full border border-purple-200 flex items-center">
                        <i className="fas fa-user-tie text-purple-600 mr-2"></i>
                        <span className="font-medium text-purple-900">
                          {selectedQuotation.salesman.employee?.name || selectedQuotation.salesman.employee}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Items/Sections */}
              <div className="mb-6">
                <h4 className="font-bold text-gray-800 mb-4 flex items-center text-lg">
                  <i className="fas fa-list mr-2 text-gray-600"></i>
                  Quotation Items
                </h4>
                {selectedQuotation.sections && selectedQuotation.sections.length > 0 ? 
                  selectedQuotation.sections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="bg-white border border-gray-200 rounded-xl mb-4 overflow-hidden shadow-sm">
                    <div className="bg-gradient-to-r from-gray-100 to-gray-50 px-6 py-4 border-b border-gray-200">
                      <h5 className="font-semibold text-gray-800 flex items-center">
                        <i className="fas fa-folder mr-2 text-gray-600"></i>
                        {section.name || `Section ${sectionIndex + 1}`}
                      </h5>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Item Details</th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Quantity</th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Unit Price</th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Discount</th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Offer Price</th>
                            <th className="px-4 py-3 text-right font-semibold text-gray-700">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {section.items && section.items.length > 0 ? section.items.map((item, itemIndex) => (
                            <tr key={itemIndex} className="border-t border-gray-100 hover:bg-gray-50">
                              <td className="px-4 py-4">
                                <div>
                                  <p className="font-medium text-gray-900 leading-tight">
                                    {item.customName || item.itemName || item.item?.skuDescription || item.item?.name || 'N/A'}
                                  </p>
                                  {item.item?.skuCode && (
                                    <p className="text-xs text-gray-500 mt-1 flex items-center">
                                      <i className="fas fa-barcode mr-1"></i>
                                      {item.item.skuCode}
                                    </p>
                                  )}
                                  {item.applicationArea && (
                                    <p className="text-xs text-blue-600 mt-1 flex items-center">
                                      <i className="fas fa-map-pin mr-1"></i>
                                      Area: {item.applicationArea}
                                    </p>
                                  )}
                                  {item.coverageArea > 0 && (
                                    <p className="text-xs text-green-600 mt-1 flex items-center">
                                      <i className="fas fa-expand-arrows-alt mr-1"></i>
                                      Coverage: {item.coverageArea} {item.coverageAreaUnit || 'sqft'}
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-right">
                                <div className="font-medium text-gray-900">
                                  {item.quantity} {item.unit || 'pcs'}
                                </div>
                                {(item.boxes > 0 || item.pieces > 0) && (
                                  <div className="text-xs text-gray-600 mt-1">
                                    {item.boxes > 0 && `${item.boxes} boxes`}
                                    {item.pieces > 0 && ` ${item.pieces} pieces`}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-4 text-right font-medium text-gray-900">
                                ₹{item.listedPrice?.toLocaleString() || '0'}
                              </td>
                              <td className="px-4 py-4 text-right">
                                <span className="text-red-600 font-medium">{item.discount || 0}%</span>
                              </td>
                              <td className="px-4 py-4 text-right font-medium text-green-600">
                                ₹{item.offerPrice?.toLocaleString() || '0'}
                              </td>
                              <td className="px-4 py-4 text-right font-bold text-gray-900">
                                ₹{(item.offerPriceTotal || item.amount)?.toLocaleString() || '0'}
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                <i className="fas fa-inbox text-2xl mb-2"></i>
                                <div>No items in this section</div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700">Section Total:</span>
                        <span className="font-bold text-lg text-blue-600">₹{section.subTotal?.toLocaleString() || '0'}</span>
                      </div>
                    </div>
                  </div>
                )) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-8 text-center">
                    <i className="fas fa-inbox text-4xl text-gray-400 mb-4"></i>
                    <h5 className="text-lg font-medium text-gray-600 mb-2">No Items Available</h5>
                    <p className="text-gray-500">This quotation doesn't have any items listed.</p>
                  </div>
                )}
              </div>

              {/* Financial Summary */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200 mb-6">
                <h4 className="font-bold text-green-900 mb-4 flex items-center text-lg">
                  <i className="fas fa-calculator mr-2"></i>
                  Financial Summary
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2">
                      <span className="text-gray-700 flex items-center">
                        <i className="fas fa-list-alt mr-2 text-gray-500 w-4"></i>
                        Subtotal:
                      </span>
                      <span className="font-semibold text-gray-900">₹{selectedQuotation.subTotal?.toLocaleString() || '0'}</span>
                    </div>
                    
                    {selectedQuotation.specialDiscount > 0 && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-700 flex items-center">
                          <i className="fas fa-percentage mr-2 text-red-500 w-4"></i>
                          Special Discount:
                        </span>
                        <span className="font-semibold text-red-600">-₹{selectedQuotation.specialDiscount?.toLocaleString() || '0'}</span>
                      </div>
                    )}
                    
                    {selectedQuotation.cartage > 0 && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-700 flex items-center">
                          <i className="fas fa-truck mr-2 text-blue-500 w-4"></i>
                          Cartage:
                        </span>
                        <span className="font-semibold text-gray-900">₹{selectedQuotation.cartage?.toLocaleString() || '0'}</span>
                      </div>
                    )}
                    
                    {selectedQuotation.labour > 0 && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-700 flex items-center">
                          <i className="fas fa-hard-hat mr-2 text-orange-500 w-4"></i>
                          Labour:
                        </span>
                        <span className="font-semibold text-gray-900">₹{selectedQuotation.labour?.toLocaleString() || '0'}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    {selectedQuotation.packingAndForwarding > 0 && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-700 flex items-center">
                          <i className="fas fa-box mr-2 text-purple-500 w-4"></i>
                          Packing & Forwarding:
                        </span>
                        <span className="font-semibold text-gray-900">₹{selectedQuotation.packingAndForwarding?.toLocaleString() || '0'}</span>
                      </div>
                    )}
                    
                    {selectedQuotation.totalGST > 0 && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-700 flex items-center">
                          <i className="fas fa-receipt mr-2 text-indigo-500 w-4"></i>
                          GST:
                        </span>
                        <span className="font-semibold text-gray-900">₹{selectedQuotation.totalGST?.toLocaleString() || '0'}</span>
                      </div>
                    )}
                    
                    {selectedQuotation.roundOff !== 0 && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-700 flex items-center">
                          <i className="fas fa-coins mr-2 text-yellow-500 w-4"></i>
                          Round Off:
                        </span>
                        <span className={`font-semibold ${selectedQuotation.roundOff > 0 ? 'text-gray-900' : 'text-red-600'}`}>
                          {selectedQuotation.roundOff > 0 ? '+' : ''}₹{selectedQuotation.roundOff?.toLocaleString() || '0'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="border-t border-green-300 mt-4 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-green-900 flex items-center">
                      <i className="fas fa-money-bill-wave mr-2"></i>
                      Total Amount:
                    </span>
                    <span className="text-2xl font-bold text-green-600">₹{selectedQuotation.totalAmount?.toLocaleString() || '0'}</span>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              {(selectedQuotation.remarks || selectedQuotation.reference || selectedQuotation.transporterName) && (
                <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-6 rounded-xl border border-gray-200">
                  <h4 className="font-bold text-gray-800 mb-4 flex items-center text-lg">
                    <i className="fas fa-info-circle mr-2"></i>
                    Additional Information
                  </h4>
                  <div className="space-y-4">
                    {selectedQuotation.remarks && (
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-start">
                          <i className="fas fa-comment-alt text-blue-600 mr-2 mt-1 w-4"></i>
                          <div>
                            <span className="font-medium text-gray-700">Remarks:</span>
                            <div className="text-gray-900 mt-1 leading-relaxed">{selectedQuotation.remarks}</div>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {selectedQuotation.reference && (
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center">
                          <i className="fas fa-user-friends text-green-600 mr-2 w-4"></i>
                          <span className="font-medium text-gray-700">Reference:</span>
                          <span className="ml-2 text-gray-900 font-medium">
                            {selectedQuotation.reference.name || selectedQuotation.reference}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {selectedQuotation.transporterName && (
                      <div className="bg-white p-4 rounded-lg border border-gray-200">
                        <div className="flex items-center">
                          <i className="fas fa-shipping-fast text-orange-600 mr-2 w-4"></i>
                          <span className="font-medium text-gray-700">Transporter:</span>
                          <span className="ml-2 text-gray-900 font-medium">
                            {selectedQuotation.transporterName.name || selectedQuotation.transporterName}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Follow-up History Modal */}
      {/* Item Disposition Modal (partial / partially-closed) */}
      {showItemsModal && selectedItemsReminder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`px-6 py-4 border-b flex items-center justify-between ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                Items
              </h3>
              <button
                onClick={() => { setShowItemsModal(false); setSelectedItemsReminder(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                    <th className="text-left py-2">Item</th>
                    <th className="text-left py-2">Qty</th>
                    <th className="text-right py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItemsReminder.details.items.map((item, idx) => (
                    <tr key={item.key || idx} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                      <td className={`py-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{item.name || item.key}</td>
                      <td className={`py-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{item.quantity ?? '-'}</td>
                      <td className="py-2 text-right">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getStatusBadgeColor(item.status)}`}>{item.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showHistoryModal && selectedReminderHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Follow-up History</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedReminderHistory.category?.replace('_', ' ')} - {getRecipientName(selectedReminderHistory)}
                </p>
              </div>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {/* Current Status */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h3 className="font-medium text-blue-900 mb-2 flex items-center">
                  <i className="fas fa-clock mr-2"></i>
                  Current Status
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Status:</span>
                    <span className={`ml-2 px-2 py-1 rounded-full text-xs ${getStatusBadgeColor(selectedReminderHistory.status)}`}>
                      {selectedReminderHistory.status}
                    </span>
                  </div>
                  <div>
                    <span className="font-medium">Date:</span> {new Date(selectedReminderHistory.date).toLocaleString()}
                  </div>
                  <div className="md:col-span-2">
                    <span className="font-medium">Message:</span> 
                    <div className="mt-1 text-gray-700">{selectedReminderHistory.message || 'No message'}</div>
                  </div>
                </div>
              </div>

              {/* History Timeline */}
              {selectedReminderHistory.updatedDetails && selectedReminderHistory.updatedDetails.length > 0 ? (
                <div>
                  <h3 className="font-medium text-gray-900 mb-4 flex items-center">
                    <i className="fas fa-history mr-2"></i>
                    Previous Follow-ups ({selectedReminderHistory.updatedDetails.length})
                  </h3>
                  <div className="space-y-4">
                    {selectedReminderHistory.updatedDetails.reverse().map((update, index) => (
                      <div key={index} className="border-l-4 border-amber-400 pl-4 pb-4">
                        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(update.previousStatus || update.status)}`}>
                                {update.previousStatus || update.status || 'Unknown'}
                              </span>
                              <span className="text-sm text-gray-600">
                                {update.refollowedAt ? new Date(update.refollowedAt).toLocaleString() : new Date(update.date).toLocaleString()}
                              </span>
                            </div>
                            {update.refollowedAt && (
                              <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded">
                                Refollowed
                              </span>
                            )}
                          </div>
                          
                          <div className="space-y-2 text-sm">
                            {(update.previousMessage || update.message) && (
                              <div>
                                <span className="font-medium text-gray-700">Message:</span>
                                <div className="text-gray-600 mt-1">"{update.previousMessage || update.message}"</div>
                              </div>
                            )}
                            
                            {update.refollowReason && (
                              <div>
                                <span className="font-medium text-gray-700">Refollow Reason:</span>
                                <div className="text-gray-600 mt-1">"{update.refollowReason}"</div>
                              </div>
                            )}
                            
                            {update.previousDate && (
                              <div>
                                <span className="font-medium text-gray-700">Original Date:</span>
                                <span className="text-gray-600 ml-2">{new Date(update.previousDate).toLocaleString()}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <i className="fas fa-history text-4xl mb-4"></i>
                  <p>No follow-up history available</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => setShowHistoryModal(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Partial Items Selection Modal */}
      {showPartialItemsModal && partialItemsData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-600 to-amber-600 text-white px-6 py-4 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold flex items-center">
                  <i className="fas fa-tasks mr-3"></i>
                  Select Items for Partial Completion
                </h3>
                <p className="text-orange-100 text-sm mt-1">
                  <i className="fas fa-hashtag mr-1"></i>
                  Quotation ID: {partialItemsData.id}
                </p>
              </div>
              <button
                onClick={() => setShowPartialItemsModal(false)}
                className="text-white hover:text-gray-200 text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-white hover:bg-opacity-20 transition-colors"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-4 text-sm text-gray-600">
                Select the items that have been partially completed. You can select multiple items across different sections.
              </div>

              {/* Items by Sections */}
              <div className="space-y-6">
                {partialItemsData.sections?.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                      <h4 className="font-semibold text-gray-800 flex items-center justify-between">
                        <span>
                          <i className="fas fa-layer-group mr-2 text-blue-600"></i>
                          {section.name || `Section ${sectionIndex + 1}`}
                        </span>
                        <span className="text-sm text-gray-500">
                          {section.items?.length || 0} items
                        </span>
                      </h4>
                    </div>
                    
                    <div className="p-4">
                      <div className="space-y-3">
                        {section.items?.map((item, itemIndex) => {
                          const itemKey = `${sectionIndex}-${itemIndex}`;
                          const isSelected = selectedPartialItems.some(
                            selected => selected.sectionIndex === sectionIndex && selected.itemIndex === itemIndex
                          );
                          
                          return (
                            <div
                              key={itemIndex}
                              className={`border rounded-lg p-3 cursor-pointer transition-all duration-200 ${
                                isSelected 
                                  ? 'border-orange-300 bg-orange-50' 
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              }`}
                              onClick={() => {
                                const itemData = {
                                  sectionIndex,
                                  itemIndex,
                                  name: item.item?.skuDescription || item.itemName || item.customName || 'Unknown Item',
                                  skuCode: item.item?.skuCode || 'N/A',
                                  quantity: item.quantity || 0,
                                  unit: item.unit || 'pcs',
                                  price: item.offerPrice || item.price || 0,
                                  total: item.offerPriceTotal || item.total || 0
                                };
                                
                                if (isSelected) {
                                  setSelectedPartialItems(prev => 
                                    prev.filter(selected => 
                                      !(selected.sectionIndex === sectionIndex && selected.itemIndex === itemIndex)
                                    )
                                  );
                                } else {
                                  setSelectedPartialItems(prev => [...prev, itemData]);
                                }
                              }}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start space-x-3 flex-1">
                                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                                    isSelected 
                                      ? 'border-orange-500 bg-orange-500' 
                                      : 'border-gray-300'
                                  }`}>
                                    {isSelected && (
                                      <i className="fas fa-check text-white text-xs"></i>
                                    )}
                                  </div>
                                  
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-800">
                                      {item.item?.skuDescription || item.itemName || item.customName || 'Unknown Item'}
                                    </div>
                                    {(item.item?.skuCode || item.skuCode) && (
                                      <div className="text-xs text-gray-500 mt-1">
                                        SKU: {item.item?.skuCode || item.skuCode}
                                      </div>
                                    )}
                                    {item.narration && (
                                      <div className="text-xs text-gray-600 mt-1 italic">
                                        {item.narration}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="text-right ml-4">
                                  <div className="text-sm font-medium text-gray-800">
                                    {item.quantity || 0} {item.unit || 'pcs'}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    ₹{(item.offerPrice || item.price || 0).toFixed(2)} each
                                  </div>
                                  <div className="text-sm font-semibold text-blue-600">
                                    ₹{(item.offerPriceTotal || item.total || 0).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Selection Summary */}
              {selectedPartialItems.length > 0 && (
                <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <h5 className="font-medium text-orange-900 mb-2 flex items-center">
                    <i className="fas fa-check-circle mr-2"></i>
                    Selected Items ({selectedPartialItems.length})
                  </h5>
                  <div className="space-y-1 text-sm">
                    {selectedPartialItems.map((item, index) => (
                      <div key={index} className="flex justify-between text-gray-700">
                        <span>{item.name}</span>
                        <span>{item.quantity} {item.unit} - ₹{item.total.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-2 border-t border-orange-200">
                    <div className="flex justify-between font-semibold text-orange-900">
                      <span>Total Selected Value:</span>
                      <span>₹{selectedPartialItems.reduce((sum, item) => sum + item.total, 0).toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                {selectedPartialItems.length} of {partialItemsData.sections?.reduce((acc, section) => acc + (section.items?.length || 0), 0) || 0} items selected
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowPartialItemsModal(false)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePartialItemsSubmit}
                  disabled={selectedPartialItems.length === 0}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  <i className="fas fa-check mr-2"></i>
                  Continue with Selected Items
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};
export default RemindersManagement;
